import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Phone, Instagram, MapPin, Star, Sparkles, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      category: "Lash & Brow",
      items: [
        { name: "Lash Lift & Tint", price: "$75" },
        { name: "Korean Lash Lift", price: "$90" },
        { name: "Classic Set", price: "$130" },
        { name: "Angel Set", price: "$135" },
        { name: "Hybrid Set", price: "$140+" },
        { name: "Volume Set", price: "$145+" },
        { name: "1 Week Fill", price: "$65" },
        { name: "2 Week Fill", price: "$80" },
        { name: "3 Week Fill", price: "$90" },
        { name: "Lash Removal", price: "$15+" },
        { name: "Eyebrow Tinting", price: "$40" },
        { name: "Brow Lamination + Tint + Shaping", price: "$90" }
      ]
    },
    {
      category: "Color Services",
      items: [
        { name: "Full Highlight", price: "$250+" },
        { name: "Balayage", price: "$250+" },
        { name: "Partial Highlight", price: "$210+" },
        { name: "Face Frame Highlight", price: "$130+" },
        { name: "Face Frame & Peekaboo Custom Color", price: "$200+" },
        { name: "Fashion Color", price: "$300+" },
        { name: "Corrective Color (Hourly Rate)", price: "$100/hr" },
        { name: "Single Process", price: "$65" },
        { name: "10 Minute Color", price: "$75" },
        { name: "Toner/Glaze", price: "$35+" },
        { name: "Lowlights", price: "$60" },
        { name: "Full Color", price: "$95" }
      ]
    },
    {
      category: "Cuts & Styling",
      items: [
        { name: "Haircut", price: "$35" },
        { name: "Haircut & Blowout", price: "$65" },
        { name: "Blowout", price: "$35" },
        { name: "Blowout & Style", price: "$45" },
        { name: "Dry Style", price: "$30" }
      ]
    },
    {
      category: "Treatments",
      items: [
        { name: "Cezanne Keratin Smoothing Treatment", price: "$380" },
        { name: "Cezanne Express Keratin Treatment", price: "$200" },
        { name: "K18 Treatment", price: "$50" },
        { name: "Olaplex Treatment", price: "$100" },
        { name: "Hair Reset Treatment", price: "$35" },
        { name: "Dream Coat Treatment", price: "$45" }
      ]
    },
    {
      category: "Facials",
      items: [
        { name: "Signature Facial", price: "$150" },
        { name: "HydroFacial for Glass Skin", price: "$175" },
        { name: "Men's Facial", price: "$125" },
        { name: "Back Facial", price: "$115" }
      ]
    },
    {
      category: "Goddess Packages",
      items: [
        { name: "Refresh & Revive Package", price: "$175+" },
        { name: "Glass & Glow Package", price: "$140+" },
        { name: "Blended & Bright Package", price: "$300+" },
        { name: "The Signature Lash & Brow Package", price: "$150" }
      ]
    },
    {
      category: "Waxing Services",
      items: [
        { name: "Eyebrow Wax", price: "$20" },
        { name: "Lip Wax", price: "$10" },
        { name: "Chin Wax", price: "$10" },
        { name: "Nose Wax", price: "$10" },
        { name: "Sideburn Wax", price: "$10" },
        { name: "Full Face Wax", price: "$55" },
        { name: "Full Leg Wax", price: "$70" },
        { name: "Upper Leg Wax", price: "$50" },
        { name: "Lower Leg Wax", price: "$40" },
        { name: "Inner Thigh Wax", price: "$20" },
        { name: "Underarm Wax", price: "$25" },
        { name: "Full Arm Wax", price: "$45" },
        { name: "Lower Arm Wax", price: "$30" },
        { name: "Shoulder Wax", price: "$28" },
        { name: "Bikini Line Wax", price: "$30" },
        { name: "Bikini Full Wax", price: "$45" },
        { name: "Bikini Brazilian Wax", price: "$55" },
        { name: "Full Butt Wax", price: "$25" },
        { name: "Butt Strip Wax", price: "$18" },
        { name: "Full Chest Wax", price: "$30" },
        { name: "Full Stomach Wax", price: "$35" },
        { name: "Stomach Strip Wax", price: "$12" },
        { name: "Full Back Wax", price: "$65" },
        { name: "Upper Back Wax", price: "$30" },
        { name: "Lower Back Wax", price: "$25" },
        { name: "Vajacial Hydrogel Mask Add-On", price: "$30" }
      ]
    }
  ];

  // Portfolio Images - Your salon transformation photos
  const portfolioImages = [
    "/images/portfolio/portfolio-1.jpg",
    "/images/portfolio/portfolio-2.jpg",
    "/images/portfolio/portfolio-3.jpg",
    "/images/portfolio/portfolio-4.jpg",
    "/images/portfolio/portfolio-5.jpg",
    "/images/portfolio/portfolio-6.jpg",
    "/images/portfolio/portfolio-7.jpg",
    "/images/portfolio/portfolio-8.jpg",
    "/images/portfolio/portfolio-9.jpg",
    "/images/portfolio/portfolio-10.jpg",
    "/images/portfolio/portfolio-11.jpg",
    "/images/portfolio/portfolio-12.jpg",
    "/images/portfolio/portfolio-13.jpg",
    "/images/portfolio/portfolio-14.jpeg",
    "/images/portfolio/portfolio-15.jpeg",
    "/images/portfolio/portfolio-16.jpeg",
    "/images/portfolio/portfolio-17.jpeg",
    "/images/portfolio/portfolio-18.jpeg",
    "/images/portfolio/portfolio-19.jpeg",
    "/images/portfolio/portfolio-20.jpeg",
    "/images/portfolio/portfolio-21.jpeg",
    "/images/portfolio/portfolio-22.jpeg",
    "/images/portfolio/portfolio-23.jpeg",
    "/images/portfolio/portfolio-24.jpeg",
  ];

  const testimonials = [
    {
      name: "Sifet Nanda",
      text: "Got a lash lift after years of wearing lash clusters and it completely transformed my lashes!! Never wearing lashes again. Def recommend Ashley she is the best !!",
      rating: 5
    },
    {
      name: "Caroline Espinoza",
      text: "Ashley is great at what she does, makes you feel so comfortable and her work is amazing, she took care of my hair so well, she does not disappoint!!",
      rating: 5
    },
    {
      name: "Marguerite Wegielski",
      text: "I love Ashley!! She got me to the blonde that i wanted and she always makes you feel so comfortable in her chair! 🩷",
      rating: 5
    },
    {
      name: "Sasha Mostafavi",
      text: "Ashley does an amazing job. She's is a very talented colorist. She blew dry my hair and stayed for DAYS! I've gotten so many compliments.",
      rating: 5
    }
  ];

  return (
    <div className="mystic-goddess-site">
      <Helmet>
        <title>Mystic Goddess Beauty Studio | Luxury Hair Salon & Transformative Beauty</title>
        <meta name="description" content="Experience mystical, transformative hair styling at Mystic Goddess Beauty Studio. Specializing in highlights, balayage, color services, and luxurious treatments. Book your transformation today." />
        <link rel="canonical" href="https://mysticgoddessbeauty.com" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="hero-section" style={{ position: 'relative' }}>
        {/* Logo in top left corner of hero section */}
        <div className="hero-logo-container" style={{
          position: 'absolute',
          top: '2rem',
          left: '2rem',
          zIndex: 100
        }}>
          <img 
            src="/logo.png" 
            alt="Mystic Goddess Logo" 
            style={{
              width: '120px',
              height: 'auto',
              filter: 'drop-shadow(0 0 20px rgba(168, 85, 247, 0.6)) drop-shadow(0 0 40px rgba(236, 72, 153, 0.4))',
              animation: 'logoGlow 3s ease-in-out infinite',
              cursor: 'pointer'
            }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          />
        </div>

        <div className="hero-overlay" />
        <div className="stars-container">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
        
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="mystic-title">MYSTIC GODDESS</h1>
            <h2 className="goddess-subtitle">Become Your Own Goddess</h2>
            <p className="hero-tagline">Radiate. Transform. Ascend.</p>
            
            <div className="hero-buttons">
              <Button 
                size="lg" 
                className="book-transformation-btn"
                onClick={() => window.open('https://mysticgoddessbeautystudio.glossgenius.com/?utm_source=ig&utm_medium=social&utm_content=link_in_bio', '_blank')}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Book Your Transformation
              </Button>
              
              <a href="tel:8458263815" className="phone-link">
                <Button size="lg" variant="outline" className="call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  (845) 826-3815
                </Button>
              </a>

              <Button 
                size="lg" 
                variant="outline"
                className="gift-card-btn"
                onClick={() => window.location.href = '/gift-cards'}
              >
                <Gift className="w-5 h-5 mr-2" />
                Buy Gift Cards
              </Button>
            </div>
          </div>
        </div>

        <div className="scroll-indicator">
          <div className="scroll-arrow" />
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="section-container">
          <div className="about-content">
            <Star className="section-icon" />
            <h2 className="section-title">Your Sacred Space for Transformation</h2>
            <p className="about-text">
              Welcome to Mystic Goddess Beauty Studio, located in the heart of Nanuet, NY—your destination for expert blonding, full highlights, and seamless balayage. We specialize in creating soft, dimensional color that enhances your natural beauty and delivers that effortless, radiant glow.
              Every service is tailored with precision and intention, giving you a transformation that feels as beautiful as it looks.
            </p>
            <div className="location-info">
              <MapPin className="w-5 h-5" />
              <span>Nanuet, NY</span>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Gallery */}
      <section className="portfolio-section">
        <div className="section-container">
          <div className="section-header">
            <Sparkles className="section-icon" />
            <h2 className="section-title">Goddess Transformations</h2>
            <p className="section-subtitle">Witness the magic of celestial hair artistry</p>
          </div>

          <div className="portfolio-grid">
            {portfolioImages.slice(0, 24).map((image, index) => (
              <div
                key={index}
                className="portfolio-item"
                onMouseEnter={() => setActiveImage(index)}
                onMouseLeave={() => setActiveImage(null)}
              >
                <img src={image} alt={`Transformation ${index + 1}`} />
                <div className={`portfolio-overlay ${activeImage === index ? 'active' : ''}`}>
                  <Sparkles className="w-8 h-8" />
                  <p>Goddess Transformation</p>
                </div>
              </div>
            ))}
          </div>

          {/* View More Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '3rem'
          }}>
            <Button
              size="lg"
              className="view-more-btn"
              onClick={() => window.location.href = '/portfolio'}
              style={{
                background: 'linear-gradient(135deg, var(--mystic-purple) 0%, #6b21a8 100%)',
                color: 'white',
                padding: '1rem 3rem',
                fontSize: '1.1rem',
                fontWeight: '600',
                borderRadius: '50px',
                border: '2px solid var(--mystic-gold)',
                boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 25px rgba(139, 92, 246, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(139, 92, 246, 0.4)';
              }}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              View Complete Portfolio
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="section-container">
          <div className="section-header">
            <Star className="section-icon" />
            <h2 className="section-title divine-offerings-title">Divine Offerings</h2>
            <p className="section-subtitle">Curated services for your transformation journey</p>
          </div>

          <div className="services-grid">
            {services.map((category, index) => (
              <Card key={index} className="service-card">
                <CardContent className="service-card-content">
                  <h3 className="service-category">{category.category}</h3>
                  <div className="service-items">
                    {category.items.map((item, idx) => (
                      <div key={idx} className="service-item">
                        <span className="service-name">{item.name}</span>
                        <span className="service-dots"></span>
                        <span className="service-price">{item.price}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="services-note">
            <p>All pricing is starting from and may vary based on hair length, density, and desired results. 
            Complimentary consultations available.</p>
          </div>
        </div>
      </section>

      {/* Gift Cards Promotion Section */}
      <section className="gift-cards-promo-section">
        <div className="section-container">
          <Card className="gift-promo-card">
            <CardContent className="gift-promo-content">
              <Gift className="gift-promo-icon" />
              <div className="gift-promo-text">
                <h2 className="gift-promo-title">Give the Gift of Transformation</h2>
                <p className="gift-promo-description">
                  Share the magic of Mystic Goddess with someone special. 
                  Perfect for birthdays, holidays, or just because they deserve to feel like a goddess.
                </p>
              </div>
              <Button 
                size="lg"
                className="gift-promo-btn"
                onClick={() => window.location.href = '/gift-cards'}
              >
                <Gift className="w-5 h-5 mr-2" />
                Shop Gift Cards
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="section-container">
          <div className="section-header">
            <Sparkles className="section-icon" />
            <h2 className="section-title">Goddess Testimonials</h2>
            <p className="section-subtitle">Hear from our radiant clients</p>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card">
                <CardContent className="testimonial-content">
                  <div className="stars-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="star-icon filled" />
                    ))}
                  </div>
                  <p className="testimonial-text">"{testimonial.text}"</p>
                  <p className="testimonial-name">— {testimonial.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-title">MYSTIC GODDESS</h3>
            <p className="footer-tagline">Beauty Studio</p>
          </div>

          <div className="footer-section">
            <h4 className="footer-heading">Contact</h4>
            <a href="tel:8458263815" className="footer-link">
              <Phone className="w-4 h-4" />
              (845) 826-3815
            </a>
            <p className="footer-text">
              <MapPin className="w-4 h-4" />
              418 Market St Suite 008, Nanuet, NY 10954
            </p>
          </div>

          <div className="footer-section">
            <h4 className="footer-heading">Follow the Magic</h4>
            <a 
              href="https://instagram.com/mysticgoddessbeautystudio" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-link instagram-link"
            >
              <Instagram className="w-5 h-5" />
              @mysticgoddessbeautystudio
            </a>
          </div>

          <div className="footer-section">
            <h4 className="footer-heading">Quick Links</h4>
            <Button 
              className="footer-book-btn"
              onClick={() => window.open('https://mysticgoddessbeautystudio.glossgenius.com/?utm_source=ig&utm_medium=social&utm_content=link_in_bio', '_blank')}
            >
              Book Your Transformation
            </Button>
            <Button 
              variant="outline"
              className="footer-gift-btn"
              onClick={() => window.location.href = '/gift-cards'}
            >
              <Gift className="w-4 h-4 mr-2" />
              Gift Cards
            </Button>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2025 Mystic Goddess Beauty Studio. All rights reserved.</p>
        </div>

        <div className="footer-stars">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="footer-star"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
      </footer>
    </div>
  );
};

export default Home;
